import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../api/adminApi';
import { MarkupProfile, CreateMarkupProfileRequest } from '../types/markup';

// Иконка редактирования (карандаш)
const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

export default function AdminMarkupsPage() {
  const [loading, setLoading] = useState(true);
  const [markupProfiles, setMarkupProfiles] = useState<MarkupProfile[]>([]);
  const [newMarkupName, setNewMarkupName] = useState('');
  const [newMarkupPercentage, setNewMarkupPercentage] = useState('');
  const [newMarkupDescription, setNewMarkupDescription] = useState('');
  const [newMarkupIsDefault, setNewMarkupIsDefault] = useState(false);
  const [editingProfile, setEditingProfile] = useState<MarkupProfile | null>(null);
  const [editMarkupName, setEditMarkupName] = useState('');
  const [editMarkupPercentage, setEditMarkupPercentage] = useState('');
  const [editMarkupDescription, setEditMarkupDescription] = useState('');
  const [editMarkupIsDefault, setEditMarkupIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'Admin') {
      navigate('/login');
      return;
    }
    fetchMarkupProfiles();
  }, [isAuthenticated, user, navigate]);

  const fetchMarkupProfiles = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllMarkupProfiles();
      if (response.success && response.data) {
        setMarkupProfiles(response.data);
      } else {
        setError(response.message || 'Ошибка при загрузке наценок');
      }
    } catch (err) {
      setError('Ошибка при загрузке наценок');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMarkupProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMarkupName || !newMarkupPercentage) {
      setError('Заполните все обязательные поля');
      return;
    }

    const percentage = parseFloat(newMarkupPercentage);
    if (isNaN(percentage) || percentage < -100 || percentage > 1000) {
      setError('Процент наценки должен быть числом от -100 до 1000');
      return;
    }

    try {
      const request: CreateMarkupProfileRequest = {
        name: newMarkupName,
        markupPercentage: percentage,
        isDefault: newMarkupIsDefault,
        description: newMarkupDescription || undefined
      };

      const response = await adminApi.createMarkupProfile(request);
      if (response.success) {
        setSuccess('Профиль наценки успешно создан');
        setNewMarkupName('');
        setNewMarkupPercentage('');
        setNewMarkupDescription('');
        setNewMarkupIsDefault(false);
        fetchMarkupProfiles(); // Обновляем список
      } else {
        setError(response.message || 'Ошибка при создании профиля наценки');
      }
    } catch (err) {
      setError('Ошибка при создании профиля наценки');
    }
  };

  const handleEditProfile = (profile: MarkupProfile) => {
    setEditingProfile(profile);
    setEditMarkupName(profile.name);
    setEditMarkupPercentage(profile.markupPercentage.toString());
    setEditMarkupDescription(profile.description || '');
    setEditMarkupIsDefault(profile.isDefault);
  };

  const handleCancelEdit = () => {
    setEditingProfile(null);
    setEditMarkupName('');
    setEditMarkupPercentage('');
    setEditMarkupDescription('');
    setEditMarkupIsDefault(false);
  };

  const handleUpdateMarkupProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProfile || !editMarkupName || !editMarkupPercentage) {
      setError('Заполните все обязательные поля');
      return;
    }

    const percentage = parseFloat(editMarkupPercentage);
    if (isNaN(percentage) || percentage < -100 || percentage > 1000) {
      setError('Процент наценки должен быть числом от -100 до 1000');
      return;
    }

    try {
      const request: CreateMarkupProfileRequest = {
        name: editMarkupName,
        markupPercentage: percentage,
        isDefault: editMarkupIsDefault,
        description: editMarkupDescription || undefined
      };

      const response = await adminApi.updateMarkupProfile(editingProfile.id, request);
      if (response.success) {
        setSuccess('Профиль наценки успешно обновлен');
        handleCancelEdit();
        fetchMarkupProfiles();
      } else {
        setError(response.message || 'Ошибка при обновлении профиля наценки');
      }
    } catch (err) {
      setError('Ошибка при обновлении профиля наценки');
    }
  };



  if (loading) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  return (
    <div className="flex w-0 flex-1 flex-col gap-6 py-10">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Форма создания наценки */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 mt-0">Создать новый профиль наценки</h2>
        <form onSubmit={handleCreateMarkupProfile} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Название *</label>
            <input
              type="text"
              value={newMarkupName}
              onChange={(e) => setNewMarkupName(e.target.value)}
              className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Процент наценки *</label>
            <input
              type="number"
              step="0.01"
              min="-100"
              max="1000"
              value={newMarkupPercentage}
              onChange={(e) => setNewMarkupPercentage(e.target.value)}
              className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Описание</label>
            <textarea
              value={newMarkupDescription}
              onChange={(e) => setNewMarkupDescription(e.target.value)}
              rows={3}
              className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isDefault"
              checked={newMarkupIsDefault}
              onChange={(e) => setNewMarkupIsDefault(e.target.checked)}
              className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
            />
            <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
              Сделать профилем по умолчанию
            </label>
          </div>
          <div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Создать профиль
            </button>
          </div>
        </form>
      </div>

      {/* Форма редактирования наценки */}
      {editingProfile && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4 mt-0">Редактировать профиль наценки</h2>
          <form onSubmit={handleUpdateMarkupProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Название *</label>
              <input
                type="text"
                value={editMarkupName}
                onChange={(e) => setEditMarkupName(e.target.value)}
                className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Процент наценки *</label>
              <input
                type="number"
                step="0.01"
                min="-100"
                max="1000"
                value={editMarkupPercentage}
                onChange={(e) => setEditMarkupPercentage(e.target.value)}
                className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Описание</label>
              <textarea
                value={editMarkupDescription}
                onChange={(e) => setEditMarkupDescription(e.target.value)}
                rows={3}
                className="p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="editIsDefault"
                checked={editMarkupIsDefault}
                onChange={(e) => setEditMarkupIsDefault(e.target.checked)}
                className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
              />
              <label htmlFor="editIsDefault" className="ml-2 block text-sm text-gray-900">
                Сделать профилем по умолчанию
              </label>
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Сохранить
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Список наценок */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium mt-0">Список профилей наценок</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Название
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Процент
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Описание
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {markupProfiles.map((profile) => (
                <tr key={profile.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {profile.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {profile.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {profile.markupPercentage}%
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {profile.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      profile.isDefault 
                        ? 'bg-gray-100 text-gray-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {profile.isDefault ? 'По умолчанию' : 'Обычный'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => handleEditProfile(profile)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                      title="Редактировать"
                    >
                      <EditIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
} 